import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { ChatContainer } from "../components/chat/ChatContainer";
import { SuggestedQuestionsOverlay } from "../components/chat/SuggestedQuestionsOverlay";
import { Header } from "../components/Header";
import { SessionGuard } from "../components/SessionGuard";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { useChatActions } from "../contexts/chat-actions-context";
import { useTodayChat } from "../hooks/useTodayChat";
import { useUser } from "../hooks/useUser";
import { useAnalytics } from "../lib/analytics";
import { api } from "../lib/api";
import { useClientTranslations } from "../lib/i18n/client";
import styles from "./ChatPage.module.css";

interface ChatHistoryResponse {
	messages: Array<{
		id: string;
		role: string;
		content: string | Array<{ text?: string }>;
		createdAt: string;
	}>;
}

function ChatPageContent() {
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();
	const analytics = useAnalytics();
	const queryClient = useQueryClient();
	const hasNavigatedToNewChat = useRef(false);
	const hasHandledLocationMessage = useRef(false);
	const { t } = useClientTranslations("chat");
	const [isOverlayOpen, setIsOverlayOpen] = useState(false);
	const { triggerMessage } = useChatActions();

	// Handle auto-send messages passed via navigation state (e.g. Ask Eve from Get Ready)
	useEffect(() => {
		const autoMessage = (location.state as Record<string, unknown> | null)
			?.autoMessage;
		if (typeof autoMessage === "string" && !hasHandledLocationMessage.current) {
			hasHandledLocationMessage.current = true;
			triggerMessage(autoMessage);
			// Clear state so back-navigation doesn't re-trigger while preserving query/hash.
			navigate(`${location.pathname}${location.search}${location.hash}`, {
				replace: true,
				state: null,
			});
		}
	}, [
		location.state,
		location.pathname,
		location.search,
		location.hash,
		triggerMessage,
		navigate,
	]);

	const handleSuggestClick = useCallback(() => {
		setIsOverlayOpen(true);
	}, []);

	const handleOverlayClose = useCallback(() => {
		setIsOverlayOpen(false);
	}, []);

	const handleQuestionSelect = useCallback(
		(
			question: string,
			metadata?: { categoryName: string; categorySlug: string },
		) => {
			triggerMessage(
				question,
				metadata
					? {
							categoryName: metadata.categoryName,
							categorySlug: metadata.categorySlug,
						}
					: undefined,
			);
		},
		[triggerMessage],
	);

	// Check if user wants to force a new chat
	const forceNew = searchParams.get("new") === "true";

	// Fetch user data with details
	const {
		data: userData,
		error: userError,
		isLoading: userLoading,
	} = useUser();

	// If no ID in URL, fetch today's chat (or force new chat if ?new=true)
	const {
		data: todayChat,
		error: todayChatError,
		isLoading: todayChatLoading,
	} = useTodayChat({
		forceNew,
		enabled: !id,
	});

	// Navigate to the new chat URL when we get a new chat from forceNew
	// This prevents the refetch issue caused by clearing params (which changes the cache key)
	useEffect(() => {
		if (forceNew && todayChat?.isNew && !hasNavigatedToNewChat.current) {
			hasNavigatedToNewChat.current = true;
			navigate(`/chat/${todayChat.chatId}`, { replace: true });
		}
	}, [forceNew, todayChat, navigate]);

	// If ID exists in URL, fetch that specific chat
	const {
		data: chatHistory,
		isLoading: chatHistoryLoading,
		error: chatHistoryError,
	} = useQuery<ChatHistoryResponse>({
		queryKey: ["chat", id],
		queryFn: async () => {
			const response = await api.get(`/api/chat/${id}`);
			return response.data;
		},
		enabled: !!id,
	});

	// Determine chat ID and messages
	const chatId = id || todayChat?.chatId || crypto.randomUUID();
	const initialMessages = id
		? chatHistory?.messages || []
		: todayChat?.messages || [];

	// Track page view
	useEffect(() => {
		analytics.logEvent("page_view", "chat", {
			chat_id: chatId,
			is_historical: String(!!id),
			is_new_chat: String(todayChat?.isNew === true),
			message_count: String(initialMessages.length),
		});
	}, [analytics, chatId, id, todayChat?.isNew, initialMessages.length]);

	// Track user data errors
	useEffect(() => {
		if (userError) {
			const errorMessage =
				userError instanceof Error ? userError.message : "User data error";
			analytics.logError(errorMessage, { context: "chat_page_user_data" });
		}
	}, [userError, analytics]);

	// Error state: user data failed
	if (userError) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<FullPageErrorState
						titleKey="error.userDataTitle"
						descriptionKey="error.userDataDescription"
						onRetry={() =>
							queryClient.invalidateQueries({ queryKey: ["user"] })
						}
						t={t}
					/>
				</main>
			</div>
		);
	}

	// Loading state: user data or chat loading
	if (userLoading || !userData || todayChatLoading || chatHistoryLoading) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<div className={styles.skeletonContainer} aria-busy="true">
						<div className={styles.skeletonBlock} />
						<div className={styles.skeletonBlock} />
						<div className={styles.skeletonBlock} />
					</div>
				</main>
			</div>
		);
	}

	// Error state: chat load failed
	const chatError = todayChatError || chatHistoryError;
	if (chatError) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<FullPageErrorState
						titleKey="error.loadTitle"
						descriptionKey="error.loadDescription"
						onRetry={() =>
							queryClient.invalidateQueries({ queryKey: ["chat"] })
						}
						t={t}
					/>
				</main>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				<ChatContainer
					id={chatId}
					user={userData}
					initialMessages={initialMessages}
					onSuggestClick={handleSuggestClick}
				/>
				<SuggestedQuestionsOverlay
					isOpen={isOverlayOpen}
					onClose={handleOverlayClose}
					onQuestionSelect={handleQuestionSelect}
					chatId={chatId}
				/>
			</main>
		</div>
	);
}

export function ChatPage() {
	return (
		<SessionGuard>
			<ChatPageContent />
		</SessionGuard>
	);
}
